import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  listStateFromSearchParams,
  listStateToSearchParams,
  loadListState,
  mergeListSearchParams,
  parsePositiveInt,
  saveListState,
} from "../utils/listNavigation";

/**
 * Restore list filters/pagination from URL + sessionStorage once on mount.
 * @param {string} listPath e.g. "/driver-management"
 * @param {(saved: object) => void} applyState receives merged URL + storage state
 */
export function useRestoreListQuery(listPath, applyState) {
  const [searchParams] = useSearchParams();
  const restoredRef = useRef(false);

  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    const fromUrl = listStateFromSearchParams(searchParams);
    const fromStorage = loadListState(listPath) || {};
    applyState({ ...fromStorage, ...fromUrl });
  }, [listPath, searchParams, applyState]);
}

/**
 * Persist list filters/pagination to sessionStorage and URL (replace).
 */
export function usePersistListQuery(listPath, state) {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    saveListState(listPath, state);
    const next = mergeListSearchParams(searchParams, state);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [listPath, state, searchParams, setSearchParams]);
}

export function readListPage(searchParams, fallback = 1) {
  return parsePositiveInt(searchParams.get("page"), fallback);
}

export function readListLimit(searchParams, fallback = 10) {
  return parsePositiveInt(searchParams.get("limit"), fallback);
}

export { mergeListSearchParams };
